/**
 * Reactive store for surfacing chat-level error status.
 *
 * Sources:
 * - GET /v1/chats/{id} returning `status === "error"`
 * - Stream completing without producing any content (silent server failure)
 *
 * The UI subscribes via useSyncExternalStore to show an inline retry indicator.
 * Consecutive empty-stream failures are tracked so the UI can escalate
 * from "try again" to "contact support" after repeated failures.
 */

type Listener = () => void;

export interface ChatErrorState {
  chatId: string;
  lastError: string;
  lastErrorAt: string;
  /** True when the error was caused by a stream that ended without content. */
  isEmptyStream?: boolean;
  /** How many empty-stream failures have occurred in a row. */
  consecutiveEmptyFailures?: number;
}

const EMPTY_STREAM_SUPPORT_THRESHOLD = 3;

class ChatErrorStore {
  private current: ChatErrorState | null = null;
  private _consecutiveEmptyFailures = 0;
  private listeners = new Set<Listener>();

  set(state: ChatErrorState) {
    this.current = state;
    this.notify();
  }

  /**
   * Record a stream that ended with [DONE] but produced no content.
   * Increments the consecutive failure counter and notifies subscribers.
   * @param forChatId Server chat id, local thread id, or other stable key used to match the active thread UI.
   */
  setEmptyStreamError(forChatId: string) {
    this._consecutiveEmptyFailures++;
    this.current = {
      chatId: forChatId,
      lastError: "empty_stream",
      lastErrorAt: new Date().toISOString(),
      isEmptyStream: true,
      consecutiveEmptyFailures: this._consecutiveEmptyFailures,
    };
    this.notify();
  }

  /** Whether the consecutive failure count has reached the support threshold. */
  get shouldSuggestSupport(): boolean {
    return this._consecutiveEmptyFailures >= EMPTY_STREAM_SUPPORT_THRESHOLD;
  }

  /** Reset the consecutive empty-stream failure counter (call on success). */
  resetEmptyFailures() {
    this._consecutiveEmptyFailures = 0;
  }

  /** Clear the current error state (does NOT reset the failure counter). */
  clear() {
    if (this.current) {
      this.current = null;
      this.notify();
    }
  }

  getSnapshot = (): ChatErrorState | null => this.current;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify() {
    this.listeners.forEach((l) => l());
  }
}

export const chatErrorStore = new ChatErrorStore();
